import React, { useRef } from 'react';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

export const MESSAGE_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp';
export const MESSAGE_ATTACHMENT_MAX_FILES = 3;
export const MESSAGE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export const formatAttachmentSize = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

export const mergeMessageAttachments = (current, incoming) => {
  const next = [...(current || [])];
  for (const file of Array.from(incoming || [])) {
    if (file.size > MESSAGE_ATTACHMENT_MAX_BYTES) throw new Error(`${file.name} is larger than 10 MB.`);
    if (!MESSAGE_ATTACHMENT_ACCEPT.split(',').some((extension) => file.name.toLowerCase().endsWith(extension))) {
      throw new Error(`${file.name} is not an allowed document type.`);
    }
    if (!next.some((item) => item.name === file.name && item.size === file.size)) next.push(file);
  }
  if (next.length > MESSAGE_ATTACHMENT_MAX_FILES) throw new Error('You can attach up to 3 files per message.');
  return next;
};

export const downloadMessageAttachment = async (api, path, fileName) => {
  const response = await api.get(path, { responseType: 'blob', timeout: 45000 });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName || 'document';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export function MessageAttachmentPicker({ files, onChange, disabled, onError }) {
  const inputRef = useRef(null);
  const choose = (event) => {
    try {
      onChange(mergeMessageAttachments(files, event.target.files));
    } catch (error) {
      onError?.(error.message);
    } finally {
      event.target.value = '';
    }
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      <input ref={inputRef} hidden type="file" multiple accept={MESSAGE_ATTACHMENT_ACCEPT} onChange={choose} />
      <Button variant="outlined" startIcon={<AttachFileRoundedIcon />} onClick={() => inputRef.current?.click()} disabled={disabled || files.length >= MESSAGE_ATTACHMENT_MAX_FILES}>
        Attach
      </Button>
      {files.length ? (
        <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${file.size}`}
              label={`${file.name} · ${formatAttachmentSize(file.size)}`}
              onDelete={disabled ? undefined : () => onChange(files.filter((_, itemIndex) => itemIndex !== index))}
              deleteIcon={<CloseRoundedIcon />}
              sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

export function MessageAttachmentList({ attachments, onDownload, light = false }) {
  if (!Array.isArray(attachments) || !attachments.length) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {attachments.map((attachment) => (
        <Button
          key={attachment.id}
          size="small"
          variant="outlined"
          startIcon={<DownloadRoundedIcon />}
          onClick={() => onDownload(attachment)}
          sx={{ justifyContent: 'flex-start', maxWidth: '100%', color: light ? 'inherit' : undefined, borderColor: light ? 'currentColor' : undefined }}
        >
          <Typography component="span" variant="caption" noWrap>{attachment.name} · {formatAttachmentSize(attachment.size)}</Typography>
        </Button>
      ))}
    </Stack>
  );
}
